import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { v4 as uuid } from 'uuid';
import Loader from '../../../components/Loader';
import SingleColGrid from '../../../components/SingleColGrid';
import { CurrentCompanyContext } from '../../../hooks/CurrentCompanyContext';
import { ROUTES } from '../../../routes/routes';
import { GatedSheetsConfiguration } from '../../../types/gatedContent';
import { GSheet } from '../../../types/gsheet';
import { StripeMode } from '../../../types/pricingPage';
import { StripePrice } from '../../../types/stripePrice';
import { useAxios } from '../../../utils/hooks';
import { enumKeys } from '../../../utils/typescript';
import { GatedContentHeader } from '../GatedContentHeader';
import ConnectGoogle from './ConnectGoogle';
import GatedSheetsForm from './GatedSheetsForm';
import ViewSheetsSnippet from './ViewSnippet';

export default function NewGatedSheet() {
    const company = useContext(CurrentCompanyContext);
    const { id: gatedContentId } = useParams<{ id: string }>();
    const axios = useAxios();
    const { addToast } = useToasts();
    const [sheets, setSheets] = useState<GSheet[]>();
    const [stripeProducts, setStripeProducts] = useState<StripePrice[]>([]);
    const [gatedSheetsConfiguration, setGatedSheetsConfiguration] = useState<GatedSheetsConfiguration>();

    const googleConnected = !!company?.google;

    useEffect(() => {
        if (googleConnected) {
            axios.current
                ?.get<{ sheets: GSheet[] }>('/google/sheets')
                .then((data) =>
                    data.data.sheets.filter(
                        (sheet) =>
                            sheet.userPermission.role === 'owner' &&
                            sheet.mimeType === 'application/vnd.google-apps.spreadsheet'
                    )
                )
                .then(setSheets);
        }
    }, [axios, googleConnected]);

    useEffect(() => {
        if (stripeProducts.length > 0) {
            return;
        }

        enumKeys(StripeMode).forEach((mode) =>
            axios.current
                ?.get<StripePrice[]>(`/stripe/products/${mode}`)
                .then(({ data }) => {
                    setStripeProducts((products) => [...products, ...data]);
                })
                .catch((err) => {
                    if (err?.response?.status !== 400) {
                        addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                    } else {
                        setStripeProducts([]);
                    }
                })
        );
    }, [addToast, axios, stripeProducts.length]);

    useEffect(() => {
        if (!axios.current) {
            return;
        }
        if (gatedContentId) {
            axios.current
                ?.get<GatedSheetsConfiguration>(`/gated-content/${gatedContentId}`)
                .then((res) => {
                    setGatedSheetsConfiguration(res.data);
                })
                .catch((err) =>
                    addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' })
                );
        } else {
            setGatedSheetsConfiguration({
                id: uuid(),
                name: '',
                gatedDomains: [],
                gatedPaths: [],
                gatedSheets: [{ sheet: {} as GSheet, stripePrices: [] }],
                authFailureRedirectUrl: '',
            });
        }
    }, [axios, addToast, gatedContentId]);

    const sendUpdateRequest = useCallback(
        debounce(
            async (newConfiguration: GatedSheetsConfiguration) =>
                axios.current
                    ?.post<GatedSheetsConfiguration>('/gated-content', newConfiguration)
                    .catch((err) =>
                        addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
                    ),
            1000
        ),
        [axios, gatedSheetsConfiguration, addToast]
    );

    const handleGoogleConnected = () => window.location.reload();

    const handleChange = async (data: GatedSheetsConfiguration) => {
        setGatedSheetsConfiguration(data);
        await sendUpdateRequest(data);
    };

    const Form = () => {
        if (!sheets || !stripeProducts) {
            return <Loader />;
        }
        return (
            <GatedSheetsForm
                sheets={sheets}
                stripeProducts={stripeProducts}
                configuration={gatedSheetsConfiguration}
                onChange={handleChange}
            />
        );
    };

    const Snippet = () => {
        if (!gatedSheetsConfiguration) {
            return <Loader />;
        }

        return <ViewSheetsSnippet gatedContent={gatedSheetsConfiguration} />;
    };

    return (
        <div className="flex flex-col w-full">
            <GatedContentHeader subtitle="Paid access to Google Sheets" />
            <SingleColGrid className="flex-1">
                {!googleConnected && (
                    <div className="flex items-start">
                        <ConnectGoogle onConnect={handleGoogleConnected} />
                    </div>
                )}
                {googleConnected && (
                    <Switch>
                        <Route exact path={ROUTES.GATED_CONTENT_SHEETS_EDIT} component={Form} />
                        <Route exact path={ROUTES.GATED_CONTENT_SHEETS} component={Form} />
                        <Route exact path={ROUTES.GATED_CONTENT_SHEETS_SNIPPET} component={Snippet} />
                    </Switch>
                )}
            </SingleColGrid>
        </div>
    );
}
